/** Mean radius of the Earth in meters, used for the haversine distance formula. */
const EARTH_RADIUS_METERS = 6_371_000;
const HALF = 2;
const FULL_CIRCLE_DEGREES = 180;

/** A geographic coordinate in decimal degrees. */
export interface LatLng {
    /** Latitude in decimal degrees. */
    lat: number;
    /** Longitude in decimal degrees. */
    lng: number;
}

/**
 * Converts an angle from degrees to radians.
 *
 * @param degrees The angle in degrees.
 * @returns The angle in radians.
 */
const toRadians = (degrees: number): number => (degrees * Math.PI) / FULL_CIRCLE_DEGREES;

/**
 * Calculates the great-circle distance between two coordinates using the haversine formula.
 *
 * @param from The first coordinate.
 * @param to   The second coordinate.
 * @returns The distance between the two coordinates in meters.
 *
 * @example
 * ```ts
 * const meters = getDistanceInMeters({lat: 48.2082, lng: 16.3738}, {lat: 48.2100, lng: 16.3800});
 * ```
 */
export const getDistanceInMeters = (from: LatLng, to: LatLng): number => {
    const deltaLat = toRadians(to.lat - from.lat);
    const deltaLng = toRadians(to.lng - from.lng);

    const a = Math.sin(deltaLat / HALF) ** HALF
        + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / HALF) ** HALF;

    return EARTH_RADIUS_METERS * HALF * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Converts a Payload `point` field (`[longitude, latitude]`) into a `LatLng` object.
 *
 * @param point The GeoJSON-style `[longitude, latitude]` tuple.
 * @returns The equivalent `LatLng` object.
 */
export const pointToLatLng = (point: [number, number]): LatLng => ({
    lat: point[1],
    lng: point[0]
});